import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Shield, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';

import { useSafeBack } from '@/utils/useSafeBack';
const LAST_UPDATED = 'March 29, 2026';

export default function PrivacyPolicyPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const goBackSafe = useSafeBack('/');
  const [dynamicContent, setDynamicContent] = useState(null);
  const [dynamicDate, setDynamicDate] = useState(null);
  const [loading, setLoading] = useState(true);

  // Per-page title + meta (audit D5.1) — restores on unmount.
  useEffect(() => {
    const prevTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute('content') : null;
    document.title = 'Privacy Policy · Eili';
    if (meta) meta.setAttribute('content', 'How Eili handles your data — what we collect, how it is used to power responses, and your rights over it.');
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc) meta.setAttribute('content', prevDesc);
    };
  }, []);

  useEffect(() => {
    async function fetchPolicy() {
      try {
        const lang = (i18n.language || 'en').split('-')[0];
        const res = await axios.get(`${API}/legal/privacy-policy`, { params: { locale: lang } });
        // Only use dynamic content when the backend served the user's actual
        // locale. If the DB only has a stale single-locale copy, prefer the
        // fully i18n-translated React fallback so Spanish/French/Portuguese
        // users never see English admin content.
        const servedLocale = res.data.content_locale || 'en';
        if (res.data.content && servedLocale === lang) {
          setDynamicContent(res.data.content);
          if (res.data.updated_at) setDynamicDate(new Date(res.data.updated_at).toLocaleDateString(lang === 'en' ? 'en-US' : lang, { year: 'numeric', month: 'long', day: 'numeric' }));
        }
      } catch { /* fall back to hardcoded */ }
      finally { setLoading(false); }
    }
    fetchPolicy();
  }, [i18n.language]);

  const dataItems = [
    { data: t('privacy.d_email'), when: t('privacy.d_email_when'), why: t('privacy.d_email_why') },
    { data: t('privacy.d_name'), when: t('privacy.d_name_when'), why: t('privacy.d_name_why') },
    { data: t('privacy.d_chat'), when: t('privacy.d_chat_when'), why: t('privacy.d_chat_why') },
    { data: t('privacy.d_history'), when: t('privacy.d_history_when'), why: t('privacy.d_history_why') },
    { data: t('privacy.d_sub'), when: t('privacy.d_sub_when'), why: t('privacy.d_sub_why') },
    { data: t('privacy.d_stripe'), when: t('privacy.d_stripe_when'), why: t('privacy.d_stripe_why') },
    { data: t('privacy.d_device'), when: t('privacy.d_device_when'), why: t('privacy.d_device_why') },
    { data: t('privacy.d_2fa'), when: t('privacy.d_2fa_when'), why: t('privacy.d_2fa_why') },
    { data: t('privacy.d_share'), when: t('privacy.d_share_when'), why: t('privacy.d_share_why') },
    { data: t('privacy.d_community'), when: t('privacy.d_community_when'), why: t('privacy.d_community_why') },
    { data: t('privacy.d_friends'), when: t('privacy.d_friends_when'), why: t('privacy.d_friends_why') },
    { data: t('privacy.d_push'), when: t('privacy.d_push_when'), why: t('privacy.d_push_why') },
    { data: t('privacy.d_shared'), when: t('privacy.d_shared_when'), why: t('privacy.d_shared_why') },
    { data: t('privacy.d_lang'), when: t('privacy.d_lang_when'), why: t('privacy.d_lang_why') },
    { data: t('privacy.d_country'), when: t('privacy.d_country_when'), why: t('privacy.d_country_why') },
    { data: t('privacy.d_session'), when: t('privacy.d_session_when'), why: t('privacy.d_session_why') },
  ];

  return (
    <div className="page-container" style={{ background: 'var(--theme-background, #0f172a)', color: 'var(--theme-text, #f1f5f9)' }}>
      <header className="sticky top-0 z-50 px-4 py-4 shadow-sm backdrop-blur-xl" style={{ background: 'var(--theme-surface, #1e293b)', borderBottom: '1px solid var(--glass-border, #334155)' }}>
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={goBackSafe} className="rounded-full h-9 w-9 p-0" data-testid="privacy-back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Shield className="h-5 w-5" style={{ color: 'var(--theme-accent, #6366f1)' }} />
          <h1 className="text-base font-semibold">{t('privacy.title')}</h1>
        </div>
      </header>
      <main className="page-content max-w-3xl mx-auto px-4 py-8 pb-20">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" style={{ color: 'var(--theme-accent)' }} /></div>
        ) : dynamicContent ? (
          <div className="space-y-6" style={{ lineHeight: 1.75 }}>
            <p className="text-xs uppercase tracking-wider" style={{ color: 'var(--theme-textSecondary, #94a3b8)' }}>
              {t('privacy.last_updated')}: {dynamicDate || LAST_UPDATED}
            </p>
            {/* Horizontally-scrollable wrapper. Inline styles used so nothing
                (Tailwind prose, parent layout) can override. Viewport-anchored
                maxWidth breaks the parent-chain growth caused by the embedded
                560px table; outer wrapper stays at viewport width and its
                overflow-x: auto makes the table swipeable. */}
            <div
              style={{
                width: '100%',
                maxWidth: 'calc(100vw - 2rem)',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                overscrollBehaviorX: 'contain',
                touchAction: 'pan-x pan-y',
              }}
              data-testid="privacy-scroll-wrapper"
            >
              <div
                className="prose prose-sm prose-invert dynamic-html-content"
                style={{ color: 'var(--theme-text, #f1f5f9)' }}
                dangerouslySetInnerHTML={{ __html: dynamicContent }}
              />
            </div>
            <div
              className="mt-6 rounded-xl p-4"
              style={{
                background: 'rgba(16,185,129,0.08)',
                border: '1px solid rgba(16,185,129,0.25)',
              }}
              data-testid="privacy-sources-block"
            >
              <p className="text-sm font-semibold mb-1" style={{ color: 'var(--theme-text)' }}>
                {t('generated.privacypolicypage_historical_cultural_source_529c')}
              </p>
              <p className="text-sm leading-relaxed mb-2" style={{ color: 'var(--theme-textSecondary)' }}>
                {t('generated.privacypolicypage_eili_uses_a_b4e4')}
              </p>
              <button
                onClick={() => navigate('/sources')}
                className="text-sm underline font-medium"
                style={{ color: '#10b981' }}
                data-testid="privacy-view-sources-link"
              >
                {t('generated.privacypolicypage_see_our_full_9152')}
              </button>
            </div>
          </div>
        ) : (
        <div className="space-y-8" style={{ lineHeight: 1.75 }}>
          <div>
            <p className="text-xs uppercase tracking-wider mb-2" style={{ color: 'var(--theme-textSecondary, #94a3b8)' }}>
              {t('privacy.last_updated')}: {LAST_UPDATED}
            </p>
            <p className="text-sm" style={{ color: 'var(--theme-textSecondary, #94a3b8)' }}>{t('privacy.intro')}</p>
          </div>

          <Section title={t('privacy.s1_title')}>
            <p>{t('privacy.s1_body')}{' '}<a href="mailto:privacy@askeili.com" className="underline" style={{ color: 'var(--theme-accent, #6366f1)' }}>{t('generated.privacypolicypage_privacy_askeili_com_d0a1')}</a>.</p>
          </Section>

          <Section title={t('privacy.s2_title')}>
            <p>{t('privacy.s2_intro')}</p>
            <DataTable items={dataItems} headers={[t('privacy.table_data'), t('privacy.table_when'), t('privacy.table_why')]} />
          </Section>

          <Section title={t('privacy.s3_title')}>
            <ul className="list-disc pl-5 space-y-1">
              {t('privacy.s3_items', { returnObjects: true }).map((item, i) => <li key={i}>{item}</li>)}
            </ul>
            <p className="mt-3">{t('privacy.s3_note')}</p>
          </Section>

          <Section title={t('privacy.s4_title')}>
            <ul className="list-disc pl-5 space-y-1">
              {t('privacy.s4_items', { returnObjects: true }).map((item, i) => <li key={i} dangerouslySetInnerHTML={{ __html: item }} />)}
            </ul>
          </Section>

          <Section title={t('privacy.s5_title')}>
            <p>{t('privacy.s5_intro')}</p>
            <div className="mt-3 space-y-3">
              {[1,2,3,4,5].map(n => (
                <ThirdParty key={n}
                  name={t(`privacy.tp${n}_name`)}
                  data={t(`privacy.tp${n}_data`)}
                  purpose={t(`privacy.tp${n}_purpose`)}
                  note={t(`privacy.tp${n}_note`)}
                  labels={[t('privacy.tp_data_shared'), t('privacy.tp_purpose')]}
                />
              ))}
            </div>
            <div
              className="mt-5 rounded-xl p-4"
              style={{
                background: 'rgba(16,185,129,0.08)',
                border: '1px solid rgba(16,185,129,0.25)',
              }}
              data-testid="privacy-sources-block"
            >
              <p className="text-sm font-semibold mb-1" style={{ color: 'var(--theme-text)' }}>
                {t('generated.privacypolicypage_historical_cultural_source_529c')}
              </p>
              <p className="text-sm leading-relaxed mb-2" style={{ color: 'var(--theme-textSecondary)' }}>
                {t('generated.privacypolicypage_eili_uses_a_b4e4')}
              </p>
              <button
                onClick={() => navigate('/sources')}
                className="text-sm underline font-medium"
                style={{ color: '#10b981' }}
                data-testid="privacy-view-sources-link"
              >
                {t('generated.privacypolicypage_see_our_full_9152')}
              </button>
            </div>
          </Section>

          <Section title={t('privacy.s6_title')}>
            <ul className="list-disc pl-5 space-y-1">
              {t('privacy.s6_items', { returnObjects: true }).map((item, i) => <li key={i}>{item}</li>)}
            </ul>
            <p className="mt-3">{t('privacy.s6_note')}</p>
          </Section>

          <Section title={t('privacy.s7_title')}>
            <ul className="list-disc pl-5 space-y-1">
              {t('privacy.s7_items', { returnObjects: true }).map((item, i) => <li key={i} dangerouslySetInnerHTML={{ __html: item }} />)}
            </ul>
          </Section>

          <Section title={t('privacy.s8_title')}>
            <p>{t('privacy.s8_intro')}</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              {t('privacy.s8_items', { returnObjects: true }).map((item, i) => <li key={i} dangerouslySetInnerHTML={{ __html: item }} />)}
            </ul>
            <p className="mt-3">
              {t('privacy.s8_note')}{' '}
              <a href="mailto:privacy@askeili.com" className="underline" style={{ color: 'var(--theme-accent, #6366f1)' }}>{t('generated.privacypolicypage_privacy_askeili_com_d0a1')}</a>.
              {' '}{t('privacy.s8_response')}
            </p>
          </Section>

          <Section title={t('privacy.s9_title')}>
            <p>{t('privacy.s9_intro')}</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              {t('privacy.s9_items', { returnObjects: true }).map((item, i) => <li key={i} dangerouslySetInnerHTML={{ __html: item }} />)}
            </ul>
            <p className="mt-3">{t('privacy.s9_note')}</p>
          </Section>

          <Section title={t('privacy.s10_title')}>
            <p>{t('privacy.s10_intro')}</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              {t('privacy.s10_items', { returnObjects: true }).map((item, i) => <li key={i} dangerouslySetInnerHTML={{ __html: item }} />)}
            </ul>
          </Section>

          <Section title={t('privacy.s11_title')}>
            <p>{t('privacy.s11_body')}{' '}<a href="mailto:privacy@askeili.com" className="underline" style={{ color: 'var(--theme-accent, #6366f1)' }}>{t('generated.privacypolicypage_privacy_askeili_com_d0a1')}</a>.</p>
          </Section>

          <Section title={t('privacy.s12_title')}>
            <p>{t('privacy.s12_intro')}</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              {t('privacy.s12_items', { returnObjects: true }).map((item, i) => <li key={i} dangerouslySetInnerHTML={{ __html: item }} />)}
            </ul>
            <p className="mt-3">{t('privacy.s12_note')}</p>
          </Section>

          <Section title={t('privacy.s13_title')}>
            <p>{t('privacy.s13_body')}</p>
          </Section>

          <Section title={t('privacy.s14_title')}>
            <p>{t('privacy.s14_body')}</p>
            <div className="mt-3 p-4 rounded-xl" style={{ background: 'var(--glass-bg, #1e293b44)', border: '1px solid var(--glass-border, #334155)' }}>
              <p className="font-medium">{t('generated.privacypolicypage_eili_2e42')}</p>
              <p><a href="mailto:privacy@askeili.com" className="underline" style={{ color: 'var(--theme-accent, #6366f1)' }}>{t('generated.privacypolicypage_privacy_askeili_com_d0a1')}</a></p>
            </div>
          </Section>
        </div>
        )}
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-lg font-semibold mb-3" style={{ color: 'var(--theme-text, #f1f5f9)' }}>{title}</h2>
      <div className="text-sm space-y-2" style={{ color: 'var(--theme-textSecondary, #cbd5e1)' }}>{children}</div>
    </section>
  );
}

function DataTable({ items, headers }) {
  // Wrap in .dynamic-html-content so the global responsive table CSS applies:
  // desktop (>900px) renders as a table; mobile/tablet portrait (<=900px)
  // auto-converts rows to stacked cards — no horizontal scrolling required.
  return (
    <div className="dynamic-html-content mt-3" data-testid="privacy-data-table">
      <table>
        <thead>
          <tr>
            {headers.map((h, i) => <th key={i}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={i}>
              <td>{item.data}</td>
              <td>{item.when}</td>
              <td>{item.why}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ThirdParty({ name, data, purpose, note, labels }) {
  return (
    <div className="p-4 rounded-xl" style={{ background: 'var(--glass-bg, #1e293b44)', border: '1px solid var(--glass-border, #334155)' }}>
      <p className="font-medium text-sm mb-1" style={{ color: 'var(--theme-text)' }}>{name}</p>
      <p className="text-xs"><strong>{labels[0]}</strong> {data}</p>
      <p className="text-xs"><strong>{labels[1]}</strong> {purpose}</p>
      <p className="text-xs mt-1 italic" style={{ color: 'var(--theme-textSecondary)' }}>{note}</p>
    </div>
  );
}
